import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LitPage } from '../components/lit-page';
import { getTheme, setTheme, type ThemeMode } from '../theming';

import '../components/luna-appearance-switcher/luna-appearance-switcher';
import '../components/luna-theme-preview/luna-theme-preview';

const THEME_MODES: readonly ThemeMode[] = ['blue', 'sand', 'slate'];

function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && (THEME_MODES as readonly string[]).includes(value);
}

@customElement('app-appearance')
export class AppAppearance extends LitPage {
  @state() private _selected: ThemeMode = getTheme();

  private onSelect(event: Event): void {
    const detail = (event as CustomEvent<{ theme?: unknown }>).detail;
    if (isThemeMode(detail?.theme)) {
      setTheme(detail.theme);
      this._selected = detail.theme;
    }
  }

  content() {
    return html`
      <div class="app-appearance">
        <section class="app-appearance__section" aria-labelledby="app-appearance-theme-title">
          <h1 class="app-appearance__title mbw-type-title-section" id="app-appearance-theme-title">
            Page theme
          </h1>
          <luna-appearance-switcher
            selected=${this._selected}
            @luna-select=${this.onSelect}
          ></luna-appearance-switcher>
          <div class="app-appearance__previews">
            ${THEME_MODES.map(
              (mode) => html`
                <luna-theme-preview
                  theme=${mode}
                  ?selected=${this._selected === mode}
                  @luna-select=${this.onSelect}
                ></luna-theme-preview>
              `
            )}
          </div>
        </section>
      </div>
    `;
  }

  styles() {
    return css`
      .app-appearance {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 26px;
        box-sizing: border-box;
      }

      .app-appearance__section {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .app-appearance__title {
        margin: 0;
        color: var(--mbw-text-ink);
      }

      .app-appearance__copy {
        margin: 0;
        color: var(--mbw-text-muted);
        max-width: 620px;
      }

      .app-appearance__previews {
        display: flex;
        flex-wrap: nowrap; /* BR-95: all three panels stay inline at desktop width */
        gap: 22px;
      }

      .app-appearance__previews luna-theme-preview {
        display: block;
        flex: 1 1 0;
        min-width: 0;
        max-width: 363px; /* pinned: figma Appearance pv panel */
        height: 270px; /* pinned: figma Appearance pv panel */
      }

      @media (max-width: 1100px) {
        .app-appearance__previews {
          flex-direction: column; /* BR-97: stack at medium + narrow viewports */
          gap: 12px;
        }

        .app-appearance__previews luna-theme-preview {
          flex: none;
          width: 100%;
          max-width: none;
          height: 77px;
        }
      }

      @media (max-width: 788px) {
        .app-appearance {
          padding: 16px;
        }
      }

      @media (max-width: 355px) {
        .app-appearance__previews luna-theme-preview {
          height: 100px;
        }
      }
    `;
  }
}
